/**
 * @file index.tsx
 *
 * @fileoverview Shows contents inside a popover. Also displays a
 * trigger for displaying the popover.
 */

import { ReactNode } from "react";
import { Trigger, Root, Content, Portal } from "@radix-ui/react-popover";
import { ColorsProps, generateColorClasses } from "../../../theme/colors";
import {
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../theme/spacing";
import styles from "./popover.module.scss";

type BasePopoverProps = SpaceProps & ColorsProps;

export interface PopoverProps extends BasePopoverProps {
  /**
   * The contents to display inside the popover.
   */
  children: ReactNode;
  /**
   * The trigger.
   */
  trigger: ReactNode;
  /**
   * Position for the popover
   *
   * @default "bottom"
   */
  position?: "left" | "right" | "top" | "bottom";
  /**
   * A maximum height for the popover.
   */
  maxHeight?: string;
  /**
   * The popover toggle callback.
   */
  onToggle?: (shown: boolean) => void;
  /**
   * The opened flag for controling component.
   * If the flag is defined, the component shown state will be ignored.
   * If a component is controlled, the onOutsideClick and onTriggerClick
   * should be used.
   */
  opened?: boolean;
  /**
   * The dismiss callback. Called when the user clicks outside of the
   * popover or presses the "Escape" key.
   * Use the callback for closing the popover
   * if the component is controlled.
   */
  onOutsideClick?: () => void;
  /**
   * The trigger click callback.
   * Use the callback for opening or closing the popover
   * if the component is controlled.
   */
  onTriggerClick?: () => void;
  /**
   * Event handler called when focus moves into the
   * component after opening. It can be prevented
   * by calling event.preventDefault.
   */
  onOpenAutoFocus?: (event: Event) => void;
  /**
   * Change the component to the HTML tag or custom component
   *  of the only child. This will merge the original component
   * props with the props of the supplied element/component
   * and change the underlying DOM node.
   *
   * @default false
   */
  triggerAsChild?: boolean;
}

export const Popover = ({
  children,
  trigger,
  position = "bottom",
  maxHeight,
  opened,
  triggerAsChild = false,
  onToggle,
  onOutsideClick,
  onTriggerClick,
  onOpenAutoFocus,
  ...props
}: PopoverProps) => {
  return (
    <>
      <Root
        open={opened}
        modal={true}
        onOpenChange={(open) => {
          onToggle?.(open);
        }}
      >
        <Trigger
          asChild={triggerAsChild}
          onClick={(e) => {
            onTriggerClick?.();
            e.stopPropagation();
          }}
        >
          {trigger}
        </Trigger>
        <Portal>
          <Content
            className={`
            ${styles.popoverContent}
            ${generateMarginClasses(props, styles)}
            ${generatePaddingClasses(props, styles)}
            ${generateColorClasses(props, styles)}
            jcl-popover-content
            `.trim()}
            onOpenAutoFocus={onOpenAutoFocus}
            onInteractOutside={() => {
              onOutsideClick && onOutsideClick();
            }}
            onEscapeKeyDown={() => {
              // Pressing "Escape" dismisses the popover just like clicking
              // outside of it does. Avoids Keyboard trap.
              onOutsideClick && onOutsideClick();
            }}
            side={position}
            sideOffset={12}
            avoidCollisions={true}
            onClick={(e) => {
              // Stop the propagation because we don't want to trigger
              // onOutsideClick callback if the popover content is clicked.
              e.stopPropagation();
            }}
            {...(maxHeight && {
              style: {
                maxHeight,
                overflowY: "auto",
              },
            })}
          >
            {children}
          </Content>
        </Portal>
      </Root>
    </>
  );
};
